import { useEffect, useRef, useState } from "react";
import useCurrentUser from "@/hooks/useCurrentUser";
import { getAgentStatusUrl } from "./constants";

export type McpPlaygroundAuthStatus = "loading" | "ready" | "unauthenticated" | "unavailable";

export function useMcpPlaygroundAuth(): { status: McpPlaygroundAuthStatus } {
  const { getAccessToken } = useCurrentUser();
  const getAccessTokenRef = useRef(getAccessToken);
  getAccessTokenRef.current = getAccessToken;
  const [status, setStatus] = useState<McpPlaygroundAuthStatus>("loading");

  useEffect(() => {
    let cancelled = false;

    async function checkStatus() {
      try {
        const token = await getAccessTokenRef.current();
        if (!token) {
          if (!cancelled) {
            setStatus("unauthenticated");
          }
          return;
        }

        const response = await fetch(getAgentStatusUrl(), {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json"
          }
        });

        if (response.status === 401) {
          if (!cancelled) {
            setStatus("unauthenticated");
          }
          return;
        }

        if (!response.ok) {
          if (!cancelled) {
            setStatus("unavailable");
          }
          return;
        }

        const body = (await response.json()) as { available?: boolean };
        if (!cancelled) {
          setStatus(body.available ? "ready" : "unavailable");
        }
      } catch {
        if (!cancelled) {
          setStatus("unavailable");
        }
      }
    }

    void checkStatus();

    return () => {
      cancelled = true;
    };
    // getAccessToken identity changes every render in Okta/token auth; read it via ref at call time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { status };
}
